import {
  applySnapshot,
  cast,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import cloneDeep from 'lodash/cloneDeep';
import { Documents } from '../../http-requests';
import { RequestStateModel } from './general-models';
import { killNulls } from '../../utils';
import { IParams } from '../../http-requests/types';
import { IObject } from '../../types';
import { ITagColors } from '../../views/authorized/pages/documents-flow/types';

const RequestsStatuses = types
  .model({
    getDocuments: types.optional(RequestStateModel, {}),
    getDocument: types.optional(RequestStateModel, {}),
    createDocument: types.optional(RequestStateModel, {}),
    editDocument: types.optional(RequestStateModel, {}),
    removeDocument: types.optional(RequestStateModel, {}),
    getCategories: types.optional(RequestStateModel, {}),
    createCategory: types.optional(RequestStateModel, {}),
    deleteCategory: types.optional(RequestStateModel, {}),
    getTags: types.optional(RequestStateModel, {}),
    createTag: types.optional(RequestStateModel, {}),
    deleteTag: types.optional(RequestStateModel, {}),
    addFiles: types.optional(RequestStateModel, {}),
    getFile: types.optional(RequestStateModel, {}),
    removeFile: types.optional(RequestStateModel, {}),
    editFile: types.optional(RequestStateModel, {}),
    allDocumentFileUpdates: types.optional(RequestStateModel, {}),
    getDocumentsQuantityByStatus: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get isSavingDocument(): boolean {
      return (
        self.createDocument.pending ||
        self.editDocument.pending ||
        self.getTags.pending
      );
    },
    get sendingFilesRequests(): boolean {
      return (
        self.editFile.pending ||
        self.removeFile.pending ||
        self.allDocumentFileUpdates.pending
      );
    },
    get isTagsPending(): boolean {
      return (
        self.getTags.pending || self.createTag.pending || self.deleteTag.pending
      );
    },
  }));

const DocStatusMST = types.enumeration('DocStatus', ['run', 'new', 'executed']);

const FiltersModel = types
  .model({
    limit: types.optional(types.number, 30),
    offset: types.optional(types.number, 0),
    status__in: types.optional(types.array(DocStatusMST), ['new', 'run']),
    category__id__in: types.optional(types.array(types.string), []),
    tag__id__in: types.optional(types.array(types.string), []),
    created_at__gte: types.optional(types.maybeNull(types.string), null),
    created_at__lte: types.optional(types.maybeNull(types.string), null),
    sector__in: types.optional(types.array(types.string), []),
    recalculation_periods__start_date__gte: types.optional(
      types.maybeNull(types.string),
      null
    ),
    recalculation_periods__end_date__lte: types.optional(
      types.maybeNull(types.string),
      null
    ),
    fias__in: types.optional(types.array(types.string), []),
    house_groups__in: types.optional(types.array(types.string), []),
    houses__in: types.optional(types.array(types.string), []),
    //Создатель
    creator__id__in: types.optional(types.array(types.string), []),
    creator__position__id__in: types.optional(types.array(types.string), []),
    creator__department__id__in: types.optional(types.array(types.string), []),
    creator__provider__id__in: types.optional(types.array(types.string), []),
    //Исполнитель
    executors__id__in: types.optional(types.array(types.string), []),
    executors__position__id__in: types.optional(types.array(types.string), []),
    executors__department__id__in: types.optional(
      types.array(types.string),
      []
    ),
    executors__provider__id__in: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get filtersCount(): number {
      const commonFilters: string[][] = [
        ['created_at__gte', 'created_at__lte'],
        [
          'recalculation_periods__start_date__gte',
          'recalculation_periods__end_date__lte',
        ],
        ['fias__in', 'house_groups__in', 'houses__in'],
      ];
      const hiddenFilters: string[] = ['limit', 'offset', 'status__in'];

      // Для подсчёта единых для фильтра полей
      let commonCount: number = 0;
      // Удаление всех пустых полей в объекте
      const fulfilledObjKeys: IObject = killNulls({ ...getSnapshot(self) });

      // [] - не пустое поле, дополнительная проверка на массив и его длину
      for (const key in fulfilledObjKeys) {
        if (
          Array.isArray(fulfilledObjKeys[key]) &&
          !fulfilledObjKeys[key].length
        ) {
          delete fulfilledObjKeys[key];
        }
      }

      // Удаление скрытых для каунтера фильтров
      hiddenFilters.forEach((key: string) => delete fulfilledObjKeys[key]);

      // Проверка на наличие общих для каунтера ключей, их удаление и увеличение commonCount
      commonFilters.forEach((filter: string[]): void => {
        if (
          Object.keys(fulfilledObjKeys).some((key: string) =>
            filter.includes(key)
          )
        ) {
          commonCount++;
          filter.forEach((key: string) => delete fulfilledObjKeys[key]);
        }
      });

      // Возвращает сумму общих для каунтера полей и кол-ва оставшихся заполненных ключей из проверенного объекта
      return commonCount + Object.keys(fulfilledObjKeys).length;
    },
    get activeDocsQuantityParams(): IObject | undefined {
      return (
        (!!this.filtersCount &&
          killNulls({ ...getSnapshot(self), status__in: ['new', 'run'] })) ||
        undefined
      );
    },
  }))
  .actions((self) => ({
    clearFilters() {
      applySnapshot(self, {
        limit: 30,
        offset: 0,
      });
    },
    setStrArrParam(
      field:
        | 'tag__id__in'
        | 'sector__in'
        | 'status__in'
        | 'category__id__in'
        | 'creator__id__in'
        | 'creator__position__id__in'
        | 'creator__department__id__in'
        | 'creator__provider__id__in'
        | 'executors__id__in'
        | 'executors__position__id__in'
        | 'executors__department__id__in'
        | 'executors__provider__id__in',
      value: string[]
    ) {
      self[field] = cast(value);
    },
    setOffset(page: number) {
      self.offset = page * self.limit - self.limit;
    },
    setDateField(
      field:
        | 'created_at__gte'
        | 'created_at__lte'
        | 'recalculation_periods__start_date__gte'
        | 'recalculation_periods__end_date__lte',
      value: string | null
    ) {
      self[field] = value;
    },
    setFias(value: string[]) {
      self.fias__in = cast(value);
    },
    setHouseGroups(value: string[]) {
      self.house_groups__in = cast(value);
    },
    setHouses(value: string[]) {
      self.houses__in = cast(value);
    },
  }));

const DocCategoryModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const CategoryModel = types
  .model({
    id: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    usage_quantity: types.optional(types.number, 0),
  })
  .views((self) => ({
    //...
  }))
  .actions((self) => ({
    //...
  }));

/* Есть идентичный тип ITagColors */
export const TagColorMST = types.enumeration('TagColor', [
  'lightGreen',
  'pink',
  'lightViolet',
  'yellow',
  'gray',
  'lightBlue',
  'violet',
  'red',
  'orange',
  'green',
  'blue',
]);

const DocTagModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const TagModel = types
  .model({
    id: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    color: types.optional(TagColorMST, 'lightGreen'),
    usage_quantity: types.optional(types.number, 0),
  })
  .views((self) => ({
    //...
  }))
  .actions((self) => ({
    //...
  }));

export interface ITagModel extends Instance<typeof TagModel> {}

const Period = types
  .model({
    start_date: types.optional(types.maybeNull(types.string), null),
    end_date: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    get isFull(): boolean {
      return !!self.start_date && !!self.end_date;
    },
    get isEmpty(): boolean {
      return !self.start_date && !self.end_date;
    },
  }))
  .actions((self) => ({
    setDateField(field: 'start_date' | 'end_date', date: string | null) {
      self[field] = date;
    },
  }));

const FiasTreeIdModel = types.model({
  id: types.optional(types.string, ''),
});

const FiasTreeAoguidModel = types.model({
  aoguid: types.optional(types.string, ''),
  address: types.optional(types.string, ''),
  aolevel: types.optional(types.number, 0),
});

const HouseModel = types.model({
  id: types.optional(types.string, ''),
  areas: types.optional(types.array(FiasTreeIdModel), []),
});

export interface IFiasTreeIdModel extends Instance<typeof FiasTreeIdModel> {}
export interface IFiasTreeAoguidModel extends Instance<
  typeof FiasTreeAoguidModel
> {}
export interface IHouseModel extends Instance<typeof HouseModel> {}

const WorkplaceModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const ProviderModel = types.model({
  id: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
});

const CreatorModel = types.model({
  id: types.optional(types.string, ''),
  department: types.optional(WorkplaceModel, {}),
  position: types.optional(WorkplaceModel, {}),
  provider: types.optional(ProviderModel, {}),
  short_name: types.optional(types.string, ''),
});

const HistoryActionMST = types.enumeration('HistoryAction', [
  'create',
  'tag',
  'status',
  'comment',
]);

const HistoryModel = types.model({
  action: types.optional(HistoryActionMST, 'create'),
  body: types.optional(types.maybeNull(types.string), null),
  timestamp: types.optional(types.string, ''),
  employee: types.optional(CreatorModel, {}),
});

const FileModel = types
  .model({
    id: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    size: types.optional(types.number, 0),
  })
  .actions((self) => ({
    setFileName(newFileName: string) {
      self.name = newFileName;
    },
  }));

export interface IFileModel extends Instance<typeof FileModel> {}

const AllHouseModel = types.model({
  address: types.optional(types.string, ''),
  areas: types.optional(types.array(types.string), []), //TODO: ТУТ ПОТОМ БУДУТ КВАРТИРЫ (НЕ СТРОКА)
  id: types.optional(types.string, ''),
});

const DocumentModel = types
  .model({
    id: types.optional(types.string, ''),
    creator: types.optional(CreatorModel, {}),
    number: types.optional(types.string, ''),
    category: types.optional(DocCategoryModel, {}),
    created_at: types.optional(types.string, ''),
    houses: types.optional(types.array(HouseModel), []),
    house_groups: types.optional(types.array(FiasTreeIdModel), []),
    fias: types.optional(types.array(FiasTreeAoguidModel), []),
    status: types.optional(DocStatusMST, 'new'),
    subject: types.optional(types.string, ''),
    description: types.optional(types.string, ''),
    sector: types.optional(types.string, ''),
    recalculation_periods: types.optional(Period, {}),
    history: types.optional(types.array(HistoryModel), []),
    tag: types.optional(types.maybeNull(DocTagModel), null),
    comment: types.optional(types.string, ''),
    files: types.optional(types.array(FileModel), []),
    all_houses: types.optional(types.array(AllHouseModel), []),
    executed_at: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    get isNewDoc(): boolean {
      return self.status === 'new';
    },
    get selectedAddressesIds(): string[] {
      return [
        ...self.house_groups.map((group: IFiasTreeIdModel) => group.id),
        ...self.houses.map((house: IHouseModel) => house.id),
      ];
    },
    get readyToSave(): boolean {
      return (
        (!!self.houses.length ||
          !!self.house_groups.length ||
          !!self.fias.length) &&
        !!self.category.id.length &&
        !!self.subject.length &&
        !!self.sector.length &&
        !!self.description.length &&
        (self.recalculation_periods.isFull ||
          self.recalculation_periods.isEmpty)
      );
    },
  }))
  .actions((self) => ({
    setField(
      field:
        | 'category'
        | 'created_at'
        | 'status'
        | 'subject'
        | 'description'
        | 'sector'
        | 'tag'
        | 'comment',
      data: any
    ) {
      self[field] = cast(cloneDeep(data));
    },
    setDateField(field: 'start_date' | 'end_date', date: string | null) {
      self.recalculation_periods.setDateField(field, date);
    },
    setFias(aoguids: IFiasTreeAoguidModel[]) {
      self.fias = cast(aoguids);
    },
    setHouseGroups(houseGroups: IFiasTreeIdModel[]) {
      self.house_groups = cast(houseGroups);
    },
    setHouses(houses: IHouseModel[]) {
      self.houses = cast(houses);
    },
    spliceFromUploadedFilesByIndex(index: number) {
      self.files.splice(index, 1);
    },
  }));

export interface IDocumentModel extends Instance<typeof DocumentModel> {}

const SortedResultModel = types.model({
  rel_date: types.optional(types.string, ''),
  date: types.optional(types.string, ''),
  result: types.optional(types.array(DocumentModel), []),
});

export interface ISortedResultModel extends Instance<
  typeof SortedResultModel
> {}

const ActiveStatusesModel = types
  .model({
    new: types.optional(types.number, 0),
    run: types.optional(types.number, 0),
  })
  .views((self) => ({
    get totalActiveCount() {
      return self.new + self.run;
    },
  }));

const DocumentsQuantityModel = types
  .model({
    active: types.optional(ActiveStatusesModel, {}),
    archive: types.optional(types.number, 0),
  })
  .views((self) => ({
    get totalCount() {
      return self.archive + self.active.totalActiveCount;
    },
  }));

const LocalFileModel = types
  .model({
    file: types.frozen<File>(),
    options: types.maybe(types.frozen<{ [propName: string]: any }>()),
  })
  .actions((self) => ({
    setNewFile(file: File) {
      self.file = file;
    },
    setOptions(options: { [propName: string]: any }) {
      self.options = options;
    },
    undefinedOptions() {
      self.options = undefined;
    },
  }));

export interface ILocalFileModel extends Instance<typeof LocalFileModel> {}

const PreparedForRenameObjectModel = types
  .model({
    id: types.optional(types.string, ''),
    newFileName: types.optional(types.string, ''),
  })
  .actions((self) => ({
    setNewFileName(newFileName: string) {
      self.newFileName = newFileName;
    },
  }));

export const DocumentsFlowStore = types
  .model({
    requestsStatuses: types.optional(RequestsStatuses, {}),
    totalCount: types.optional(types.number, 0),
    documents: types.optional(types.array(SortedResultModel), []),
    documentsQuantityByStatus: types.optional(DocumentsQuantityModel, {}),
    currentDocument: types.optional(DocumentModel, {}),
    initialCurrentDocument: types.optional(DocumentModel, {}),
    filters: types.optional(FiltersModel, {}),
    //lastSearchCounterFilters: types.optional(types.number, 0), //TODO: не используется
    categories: types.optional(types.array(CategoryModel), []),
    tags: types.optional(types.array(TagModel), []),
    isOpenCreateModal: types.optional(types.boolean, false),
    isOpenDocumentCardModal: types.optional(types.boolean, false),
    isOpenCategoriesModal: types.optional(types.boolean, false),
    localFiles: types.optional(types.array(LocalFileModel), []),
    filesToRename: types.optional(
      types.array(PreparedForRenameObjectModel),
      []
    ),
    /*
      fileIdsToDelete - перечень id ранее загруженных файлов, помеченных на
      удаление. Будут удалены в самом документе и в chunks при сохранении
      документа в интерфейсе
    */
    fileIdsToDelete: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get formattedCategories() {
      //для селекта
      return self.categories.map((category) => {
        return {
          label: category.name,
          value: category.id,
        };
      });
    },
    get formattedTags() {
      //для селекта
      return self.tags.map((tag) => {
        return {
          label: tag.name,
          value: tag.id,
        };
      });
    },
    get usedCategories() {
      return self.categories.filter((item) => !!item.usage_quantity);
    },
    get newCategories() {
      return self.categories.filter((item) => !item.usage_quantity);
    },
    get unmodifiedCurrentDocument() {
      const { files: currentFiles, ...resultObj } = self.currentDocument;
      const { files: initialFiles, ...initialObj } =
        self.initialCurrentDocument;

      return JSON.stringify(resultObj) === JSON.stringify(initialObj);
    },
    get unmodifiedDocumentFiles() {
      return (
        !self.filesToRename.length &&
        !self.fileIdsToDelete.length &&
        !self.localFiles.length
      );
    },
    get disableSave() {
      return (
        (this.unmodifiedCurrentDocument && this.unmodifiedDocumentFiles) ||
        self.requestsStatuses.allDocumentFileUpdates.pending ||
        self.requestsStatuses.getDocument.pending ||
        !self.currentDocument.readyToSave
        // ||
        // (!self.localFiles.length && !self.currentDocument.files.length)
      );
    },
    get comparedCurrentDocument() {
      /* Получаем только отличающиеся поля при редактировании документа */
      const resultObj: { [key: string]: any } = {
        ...getSnapshot(self.currentDocument),
      };
      const initialObj: { [key: string]: any } = {
        ...getSnapshot(self.initialCurrentDocument),
      };

      for (const key in resultObj) {
        if (
          JSON.stringify(resultObj[key]) === JSON.stringify(initialObj[key])
        ) {
          delete resultObj[key];
        }
      }

      return resultObj;
    },
    isUniqueTag(value: string): boolean {
      return !self.tags.some(
        (tag: ITagModel) =>
          tag.name.toLowerCase() === value.toLowerCase().trim()
      );
    },
  }))
  .actions((self) => ({
    getDocumentsQuantityByStatus: flow(function* (params?: IObject) {
      try {
        self.requestsStatuses.getDocumentsQuantityByStatus.pending = true;
        self.requestsStatuses.getDocumentsQuantityByStatus.error = false;
        self.requestsStatuses.getDocumentsQuantityByStatus.fulfilled = false;

        const response = yield Documents.GetDocumentsQuantityByStatus(params);
        self.documentsQuantityByStatus = response;

        self.requestsStatuses.getDocumentsQuantityByStatus.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getDocumentsQuantityByStatus.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getDocumentsQuantityByStatus.pending = false;
      }
    }),
  }))
  .actions((self) => ({
    setFullDocumentInList(data: IDocumentModel) {
      const initial = cloneDeep(self.documents);
      const formatted = initial.map((group: ISortedResultModel) => {
        return {
          ...group,
          result: [...group.result].map((item: IDocumentModel) => {
            if (!!data?.id && data.id === item.id) {
              return { ...data };
            }

            return { ...item };
          }),
        };
      });

      self.documents = cast(formatted);
    },
    setCurrentDocument(data: IDocumentModel) {
      self.currentDocument = cloneDeep(data);
      self.initialCurrentDocument = cloneDeep(data);
    },
    clearStore() {
      applySnapshot(self, {});
    },
    clearCurrentDocument() {
      applySnapshot(self.currentDocument, {});
      applySnapshot(self.initialCurrentDocument, {});
    },
    clearPreparedToRenameFiles() {
      applySnapshot(self.filesToRename, []);
    },
    clearFileIdsToDelete() {
      applySnapshot(self.fileIdsToDelete, []);
    },
    clearLocalFiles() {
      applySnapshot(self.localFiles, []);
    },
    setOpenModal(
      key:
        | 'isOpenCreateModal'
        | 'isOpenDocumentCardModal'
        | 'isOpenCategoriesModal',
      bool: boolean
    ) {
      self[key] = bool;

      if (key === 'isOpenCreateModal' && bool) {
        self.currentDocument = DocumentModel.create();
      }
    },
    pushLocalFile(file: File) {
      self.localFiles.push({ file });
    },
    spliceLocalFileByIndex(
      index: number,
      fileObj?: { file: File; otherInformation?: { [propName: string]: any } }
    ) {
      if (fileObj) {
        self.localFiles[index].setNewFile(fileObj.file);
        self.localFiles[index].undefinedOptions();
      } else {
        self.localFiles.splice(index, 1);
      }
    },
    prepareToRenameUploadedFile(
      index: number,
      fileId: string,
      newFileName: string
    ) {
      self.currentDocument.files[index].setFileName(newFileName);
      const includedObjIndex = self.filesToRename.findIndex(
        (obj) => obj.id === fileId
      );

      if (includedObjIndex >= 0) {
        self.filesToRename[includedObjIndex].setNewFileName(newFileName);
      } else {
        self.filesToRename.push({
          id: fileId,
          newFileName,
        });
      }
    },
    deleteDocumentFile(index: number, fileId: string, instantUpdate?: boolean) {
      if (!instantUpdate) {
        self.currentDocument.spliceFromUploadedFilesByIndex(index);
      }
      const deletingFileIndex = self.filesToRename.findIndex(
        (obj) => obj.id === fileId
      );

      if (deletingFileIndex !== -1) {
        self.filesToRename.splice(deletingFileIndex, 1);
      }

      self.fileIdsToDelete.push(fileId);
    },
    getDocuments: flow(function* (number__startswith?: string) {
      try {
        self.requestsStatuses.getDocuments.pending = true;
        self.requestsStatuses.getDocuments.error = false;
        self.requestsStatuses.getDocuments.fulfilled = false;

        //self.lastSearchCounterFilters = self.filters.filtersCount; //TODO: не используется

        self.getDocumentsQuantityByStatus(
          !!number__startswith
            ? { number__startswith }
            : self.filters.activeDocsQuantityParams
        );

        const response = yield Documents.GetDocuments(
          !!number__startswith
            ? { number__startswith }
            : killNulls(self.filters)
        );
        self.documents = response.results;
        self.totalCount = response.count;

        if (!!number__startswith && !!response.results.length) {
          self.filters.setStrArrParam('status__in', [
            response.results[0].result[0].status,
          ]);
        }

        self.requestsStatuses.getDocuments.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getDocuments.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getDocuments.pending = false;
      }
    }),
    getDocument: flow(function* (id: string) {
      try {
        self.requestsStatuses.getDocument.pending = true;
        self.requestsStatuses.getDocument.error = false;
        self.requestsStatuses.getDocument.fulfilled = false;

        const response = yield Documents.GetDocument(id);
        self.currentDocument = response;
        self.initialCurrentDocument = response;

        self.requestsStatuses.getDocument.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getDocument.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getDocument.pending = false;
      }
    }),
    createDocument: flow(function* () {
      try {
        self.requestsStatuses.createDocument.pending = true;
        self.requestsStatuses.createDocument.error = false;
        self.requestsStatuses.createDocument.fulfilled = false;

        const params = killNulls(self.currentDocument);

        if (!params?.recalculation_periods) {
          params.recalculation_periods =
            self.currentDocument.recalculation_periods;
        }

        const response = yield Documents.CreateDocument(params);
        self.requestsStatuses.createDocument.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.createDocument.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createDocument.pending = false;
      }
    }),
    updateDocument: flow(function* () {
      try {
        //нужно отправлять только те поля, которые поменялись
        self.requestsStatuses.editDocument.pending = true;
        self.requestsStatuses.editDocument.error = false;
        self.requestsStatuses.editDocument.fulfilled = false;

        const response = yield Documents.EditDocument(
          self.currentDocument.id,
          self.comparedCurrentDocument
        );
        self.requestsStatuses.editDocument.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.editDocument.error = true;
        console.error(e);

        throw e;
      } finally {
        self.requestsStatuses.editDocument.pending = false;
      }
    }),
    removeDocument: flow(function* () {
      try {
        self.requestsStatuses.removeDocument.pending = true;
        self.requestsStatuses.removeDocument.error = false;
        self.requestsStatuses.removeDocument.fulfilled = false;

        const response = yield Documents.RemoveDocument(
          self.currentDocument.id
        );
        self.requestsStatuses.removeDocument.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.removeDocument.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.removeDocument.pending = false;
      }
    }),
    getCategories: flow(function* () {
      try {
        self.requestsStatuses.getCategories.pending = true;
        self.requestsStatuses.getCategories.error = false;
        self.requestsStatuses.getCategories.fulfilled = false;

        const response = yield Documents.GetCategories();
        self.categories = response;
        self.requestsStatuses.getCategories.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getCategories.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getCategories.pending = false;
      }
    }),
    createCategory: flow(function* (data: IParams) {
      try {
        self.requestsStatuses.createCategory.pending = true;
        self.requestsStatuses.createCategory.error = false;
        self.requestsStatuses.createCategory.fulfilled = false;

        const response = yield Documents.CreateCategory(data);
        self.requestsStatuses.createCategory.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.createCategory.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createCategory.pending = false;
      }
    }),
    deleteCategory: flow(function* (id: string) {
      try {
        self.requestsStatuses.deleteCategory.pending = true;
        self.requestsStatuses.deleteCategory.error = false;
        self.requestsStatuses.deleteCategory.fulfilled = false;

        const response = yield Documents.DeleteCategory(id);
        self.requestsStatuses.deleteCategory.fulfilled = true;
        self.categories = cast(
          self.categories.filter((item) => item.id !== id)
        );

        return response;
      } catch (e) {
        self.requestsStatuses.deleteCategory.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.deleteCategory.pending = false;
      }
    }),
    getTags: flow(function* () {
      try {
        self.requestsStatuses.getTags.pending = true;
        self.requestsStatuses.getTags.error = false;
        self.requestsStatuses.getTags.fulfilled = false;

        const response = yield Documents.GetTags();
        self.tags = response;
        self.requestsStatuses.getTags.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getTags.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getTags.pending = false;
      }
    }),
    createTag: flow(function* (data: { name: string; color: ITagColors }) {
      try {
        self.requestsStatuses.createTag.pending = true;
        self.requestsStatuses.createTag.error = false;
        self.requestsStatuses.createTag.fulfilled = false;

        data.name.trim();
        const response = yield Documents.CreateTag(data);
        self.requestsStatuses.createTag.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.createTag.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createTag.pending = false;
      }
    }),
    deleteTag: flow(function* (id: string) {
      try {
        self.requestsStatuses.deleteTag.pending = true;
        self.requestsStatuses.deleteTag.error = false;
        self.requestsStatuses.deleteTag.fulfilled = false;

        const response = yield Documents.DeleteTag(id);
        self.requestsStatuses.deleteTag.fulfilled = true;
        self.tags = cast(self.tags.filter((item) => item.id !== id));

        return response;
      } catch (e) {
        self.requestsStatuses.deleteTag.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.deleteTag.pending = false;
      }
    }),
    addFiles: flow(function* (data: FormData) {
      try {
        self.requestsStatuses.addFiles.pending = true;
        self.requestsStatuses.addFiles.error = false;
        self.requestsStatuses.addFiles.fulfilled = false;

        const response = yield Documents.AddFile(self.currentDocument.id, data);

        self.requestsStatuses.addFiles.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.addFiles.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.addFiles.pending = false;
      }
    }),
    getFile: flow(function* (fileId: string) {
      try {
        self.requestsStatuses.getFile.pending = true;
        self.requestsStatuses.getFile.error = false;
        self.requestsStatuses.getFile.fulfilled = false;

        const response = yield Documents.GetFile(
          self.currentDocument.id,
          fileId
        );

        self.requestsStatuses.getFile.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.getFile.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getFile.pending = false;
      }
    }),
    removeFile: flow(function* (fileId: string) {
      try {
        self.requestsStatuses.removeFile.pending = true;
        self.requestsStatuses.removeFile.error = false;
        self.requestsStatuses.removeFile.fulfilled = false;

        const response = yield Documents.RemoveFile(
          self.currentDocument.id,
          fileId
        );

        self.requestsStatuses.removeFile.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.removeFile.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.removeFile.pending = false;
      }
    }),
    editFile: flow(function* (fileId: string, newName: string) {
      try {
        self.requestsStatuses.editFile.pending = true;
        self.requestsStatuses.editFile.error = false;
        self.requestsStatuses.editFile.fulfilled = false;

        const response = yield Documents.EditFile(
          self.currentDocument.id,
          fileId,
          {
            new_filename: newName,
          }
        );

        self.requestsStatuses.editFile.fulfilled = true;

        return response;
      } catch (e) {
        self.requestsStatuses.editFile.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.editFile.pending = false;
      }
    }),
    allDocumentFileUpdates: flow(function* (docId?: string) {
      self.requestsStatuses.allDocumentFileUpdates.pending = true;
      self.requestsStatuses.allDocumentFileUpdates.error = false;
      self.requestsStatuses.allDocumentFileUpdates.fulfilled = false;

      const documentId = docId || self.currentDocument.id;

      try {
        const promises = [
          ...self.filesToRename.map((model) => {
            return Documents.EditFile(documentId, model.id, {
              new_filename: model.newFileName,
            });
          }),
          ...self.fileIdsToDelete.map((fileId) => {
            return Documents.RemoveFile(documentId, fileId);
          }),
        ];

        if (self.localFiles.length) {
          const fd = new FormData();
          self.localFiles.forEach((model, index) => {
            fd.set(`file_${index}`, model.file);
          });
          promises.push(Documents.AddFile(documentId, fd));
        }

        yield Promise.allSettled(promises);
        self.requestsStatuses.allDocumentFileUpdates.fulfilled = true;
      } catch (e) {
        self.requestsStatuses.allDocumentFileUpdates.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.allDocumentFileUpdates.pending = false;
      }
    }),
  }));
